import React, { useMemo, useState } from "react";
import { Landmark, Loader2, Check, ShieldCheck, Wallet, CalendarClock } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { crm } from "@/lib/crm";
import { computeMonthly, buildSeries, clampNum, eur } from "@/lib/financing";
import { toast } from "sonner";

// Elite Platinum indicative credit / leasing simulator, opened from a listing.
export default function FinancingSimulatorDialog({ car, sellerId, config, onClose }) {
  const price = Number(car?.price) || 0;
  const durations = (config?.durations?.length ? config.durations : [24, 36, 48, 60]);
  const taeg = Number(config?.taeg) || 4.99;
  const minDownPct = Number(config?.min_down_pct) || 0;
  const residualPct = Number(config?.residual_pct) || 30;
  const minDown = Math.round((price * minDownPct) / 100);

  const [mode, setMode] = useState("credit");
  const [down, setDown] = useState(minDown);
  const [months, setMonths] = useState(durations[Math.min(2, durations.length - 1)] || 48);
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });

  const args = { price, down, taeg, months, residualPct };
  const res = useMemo(() => computeMonthly(mode, args), [mode, price, down, taeg, months, residualPct]);
  const series = useMemo(() => buildSeries(mode, args), [mode, price, down, taeg, months, residualPct]);

  const carLabel = `${car?.make || ""} ${car?.model || ""}${car?.year ? " · " + car.year : ""}`.trim();

  const submit = async () => {
    if (!form.name.trim() || !form.email.includes("@")) { toast.error("Renseignez votre nom et un e-mail valide."); return; }
    setBusy(true);
    try {
      await crm("/financing/leads", { method: "POST", body: {
        car_id: String(car?.id || ""), seller_id: sellerId, car_label: carLabel,
        name: form.name, email: form.email, phone: form.phone, message: form.message,
        mode, price, down_payment: down, duration: months, monthly: Math.round(res.monthly),
        taeg, residual: Math.round(res.buyback || 0),
      } });
      setSent(true);
    } catch (e) { toast.error(e.message || "Envoi impossible"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="elite-platinum max-w-3xl overflow-hidden border-[#C9D2DC]/25 bg-[#0b1b2e] p-0" data-testid="financing-simulator">
        <div className="relative">
          <div className="plat-sheen pointer-events-none absolute inset-x-0 top-0 h-24 opacity-40" />
          <DialogHeader className="border-b border-white/10 px-6 py-5">
            <DialogTitle className="flex items-center gap-2 font-display text-xl text-white">
              <Landmark size={20} className="text-[#C9D2DC]" /> Simulateur de financement
            </DialogTitle>
            <DialogDescription className="text-white/55">{carLabel} · {eur(price)}</DialogDescription>
          </DialogHeader>
        </div>

        <div className="grid gap-6 px-6 py-5 md:grid-cols-2">
          {/* Controls */}
          <div className="space-y-5">
            <div className="inline-flex rounded-xl border border-white/12 bg-white/[0.04] p-1" role="tablist">
              {[["credit", "Crédit"], ["leasing", "Leasing / LOA"]].map(([k, l]) => (
                <button key={k} data-testid={`fin-mode-${k}`} onClick={() => setMode(k)}
                  className={`rounded-lg px-4 py-1.5 text-sm font-bold transition ${mode === k ? "plat-btn" : "text-white/60 hover:text-white"}`}>{l}</button>
              ))}
            </div>

            <div>
              <div className="mb-2 flex items-center justify-between text-sm">
                <span className="flex items-center gap-1.5 font-semibold text-white/80"><Wallet size={14} className="text-[#C9D2DC]" /> Apport</span>
                <span className="font-bold text-white" data-testid="fin-down-value">{eur(down)}</span>
              </div>
              <Slider data-testid="fin-down-slider" value={[down]} min={minDown} max={Math.round(price * 0.9)} step={100}
                onValueChange={(v) => setDown(clampNum(v[0], minDown, price * 0.9))} />
              {minDownPct > 0 && <p className="mt-1 text-[11px] text-white/40">Apport minimum requis : {minDownPct}% ({eur(minDown)})</p>}
            </div>

            <div>
              <div className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-white/80"><CalendarClock size={14} className="text-[#C9D2DC]" /> Durée</div>
              <div className="flex flex-wrap gap-2">
                {durations.map((d) => (
                  <button key={d} data-testid={`fin-duration-${d}`} onClick={() => setMonths(d)}
                    className={`rounded-lg border px-3 py-1.5 text-sm font-bold transition ${months === d ? "plat-btn border-transparent" : "border-white/12 bg-white/[0.04] text-white/65 hover:text-white"}`}>{d} mois</button>
                ))}
              </div>
            </div>

            <div className="rounded-2xl border border-[#C9D2DC]/25 bg-gradient-to-b from-[#123A63]/40 to-white/[0.02] p-4">
              <div className="text-xs uppercase tracking-wide text-white/50">Mensualité estimée</div>
              <div className="mt-1 font-display text-4xl font-extrabold plat-chrome" data-testid="fin-monthly">{eur(res.monthly)}<span className="text-lg font-bold text-white/60"> /mois</span></div>
              <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                <div className="rounded-lg bg-white/[0.04] px-2.5 py-1.5"><div className="text-white/45">TAEG</div><div className="font-bold text-white">{taeg}%</div></div>
                <div className="rounded-lg bg-white/[0.04] px-2.5 py-1.5"><div className="text-white/45">{mode === "leasing" ? "Option d'achat" : "Coût du crédit"}</div><div className="font-bold text-white">{eur(mode === "leasing" ? res.buyback : res.interest)}</div></div>
              </div>
            </div>
          </div>

          {/* Chart + lead */}
          <div className="space-y-4">
            <div className="rounded-2xl border border-white/10 bg-[#0d2036] p-3">
              <div className="mb-1 px-1 text-xs font-semibold text-white/55">{mode === "leasing" ? "Cumul payé vs valeur de rachat" : "Capital restant dû"}</div>
              <div className="h-[150px]">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={series} margin={{ top: 6, right: 8, left: -18, bottom: 0 }}>
                    <defs>
                      <linearGradient id="finArea" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#C9D2DC" stopOpacity={0.55} />
                        <stop offset="100%" stopColor="#C9D2DC" stopOpacity={0.04} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                    <XAxis dataKey="m" tick={{ fill: "rgba(255,255,255,0.4)", fontSize: 10 }} tickFormatter={(v) => `${v}m`} />
                    <YAxis tick={{ fill: "rgba(255,255,255,0.4)", fontSize: 10 }} tickFormatter={(v) => `${Math.round(v / 1000)}k`} />
                    <Tooltip contentStyle={{ background: "#0b1b2e", border: "1px solid rgba(201,210,220,0.25)", borderRadius: 10, color: "#fff", fontSize: 12 }}
                      formatter={(v) => eur(v)} labelFormatter={(l) => `Mois ${l}`} />
                    <Area type="monotone" dataKey={mode === "leasing" ? "paid" : "capital"} stroke="#C9D2DC" strokeWidth={2} fill="url(#finArea)" isAnimationActive animationDuration={500} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

            {sent ? (
              <div className="flex flex-col items-center rounded-2xl border border-emerald-400/30 bg-emerald-400/[0.06] px-4 py-6 text-center" data-testid="fin-lead-sent">
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-emerald-500/20 text-emerald-300"><Check size={22} /></span>
                <div className="mt-3 font-display font-bold text-white">Demande envoyée !</div>
                <p className="mt-1 text-sm text-white/60">Le concessionnaire vous recontacte rapidement avec une offre personnalisée.</p>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  <Input data-testid="fin-lead-name" placeholder="Nom complet" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-10 bg-white/[0.05]" />
                  <Input data-testid="fin-lead-phone" placeholder="Téléphone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="h-10 bg-white/[0.05]" />
                </div>
                <Input data-testid="fin-lead-email" type="email" placeholder="E-mail" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-10 bg-white/[0.05]" />
                <Button data-testid="fin-lead-submit" onClick={submit} disabled={busy} className="h-11 w-full gap-2 plat-btn font-bold">
                  {busy ? <Loader2 size={16} className="animate-spin" /> : <ShieldCheck size={16} />} Recevoir mon offre de financement
                </Button>
                <p className="text-[10px] leading-snug text-white/35">Simulation fournie à titre indicatif, sans valeur contractuelle, sous réserve d'acceptation du dossier par un organisme de crédit agréé.</p>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
